/**
 * Create Graphic Page - Kainva Integration
 * AI-powered social media graphic generation
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBrand } from '../contexts/BrandContext';
import { apiClient } from '../lib/api';
import { Card, CardHeader } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Textarea } from '../components/ui/Textarea';
import { Icon } from '../components/icons';
import type { SocialPlatform } from '../types';

type AspectRatio = '1:1' | '4:5' | '9:16' | '16:9' | '1.91:1';
type GraphicStyle = 'modern' | 'minimal' | 'bold' | 'elegant' | 'playful' | 'corporate';

interface GeneratedGraphic {
  id: string;
  imageUrl: string;
  prompt: string;
  aspectRatio: AspectRatio;
  style: GraphicStyle;
  createdAt: number;
}

const ASPECT_RATIOS: { value: AspectRatio; label: string; description: string; platforms: string }[] = [
  { value: '1:1', label: 'Square (1:1)', description: '1080x1080', platforms: 'Instagram, Facebook, Twitter' },
  { value: '4:5', label: 'Portrait (4:5)', description: '1080x1350', platforms: 'Instagram Feed' },
  { value: '9:16', label: 'Story (9:16)', description: '1080x1920', platforms: 'Stories, Reels, TikTok' },
  { value: '16:9', label: 'Landscape (16:9)', description: '1920x1080', platforms: 'YouTube, Twitter, LinkedIn' },
  { value: '1.91:1', label: 'Wide (1.91:1)', description: '1200x628', platforms: 'Facebook Link, LinkedIn' },
];

const GRAPHIC_STYLES: { value: GraphicStyle; label: string; description: string }[] = [
  { value: 'modern', label: 'Modern', description: 'Clean lines, contemporary feel' },
  { value: 'minimal', label: 'Minimal', description: 'Simple, lots of whitespace' },
  { value: 'bold', label: 'Bold', description: 'Strong colors, impactful' },
  { value: 'elegant', label: 'Elegant', description: 'Sophisticated, refined' },
  { value: 'playful', label: 'Playful', description: 'Fun, energetic, colorful' },
  { value: 'corporate', label: 'Corporate', description: 'Professional, business-ready' },
];

const PLATFORM_OPTIONS: { value: SocialPlatform; label: string }[] = [
  { value: 'instagram', label: 'Instagram' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'twitter', label: 'Twitter/X' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'tiktok', label: 'TikTok' },
];

export function CreateGraphic() {
  const { selectedBrand } = useBrand();
  const navigate = useNavigate();

  // Form state
  const [prompt, setPrompt] = useState('');
  const [headline, setHeadline] = useState('');
  const [subheadline, setSubheadline] = useState('');
  const [aspectRatio, setAspectRatio] = useState<AspectRatio>('1:1');
  const [style, setStyle] = useState<GraphicStyle>('modern');
  const [platforms, setPlatforms] = useState<SocialPlatform[]>(['instagram']);
  const [includeText, setIncludeText] = useState(true);
  const [useBrandColors, setUseBrandColors] = useState(true);

  // UI state
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedGraphics, setGeneratedGraphics] = useState<GeneratedGraphic[]>([]);
  const [selectedGraphic, setSelectedGraphic] = useState<GeneratedGraphic | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleGenerate = async () => {
    if (!selectedBrand || !prompt.trim()) {
      setError('Please enter a description for your graphic');
      return;
    }

    setIsGenerating(true);
    setError('');

    try {
      // Build the full prompt with brand context
      const fullPrompt = `Create a ${style} social media graphic for ${selectedBrand.name}.
        ${prompt}
        ${headline ? `Main headline: "${headline}"` : ''}
        ${subheadline ? `Subheadline: "${subheadline}"` : ''}
        ${useBrandColors ? `Use brand colors: primary ${selectedBrand.color_primary}, secondary ${selectedBrand.color_secondary}` : ''}
        Aspect ratio: ${aspectRatio}
        Target platforms: ${platforms.join(', ')}
        ${includeText ? 'Include text overlays' : 'Minimal or no text'}`;

      // For now, simulate the generation since we don't have actual Kainva API
      // In production, this would call the Kainva API
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Generate mock results (in production, these would come from Kainva API)
      const mockGraphics: GeneratedGraphic[] = [
        {
          id: `graphic-${Date.now()}-1`,
          imageUrl: `https://placehold.co/${aspectRatio === '1:1' ? '600x600' : aspectRatio === '4:5' ? '540x675' : aspectRatio === '9:16' ? '405x720' : aspectRatio === '16:9' ? '800x450' : '764x400'}/${selectedBrand.color_primary.replace('#', '')}/${selectedBrand.color_secondary.replace('#', '')}?text=Variation+1`,
          prompt: fullPrompt,
          aspectRatio,
          style,
          createdAt: Date.now(),
        },
        {
          id: `graphic-${Date.now()}-2`,
          imageUrl: `https://placehold.co/${aspectRatio === '1:1' ? '600x600' : aspectRatio === '4:5' ? '540x675' : aspectRatio === '9:16' ? '405x720' : aspectRatio === '16:9' ? '800x450' : '764x400'}/${selectedBrand.color_secondary.replace('#', '')}/${selectedBrand.color_primary.replace('#', '')}?text=Variation+2`,
          prompt: fullPrompt,
          aspectRatio,
          style,
          createdAt: Date.now(),
        },
        {
          id: `graphic-${Date.now()}-3`,
          imageUrl: `https://placehold.co/${aspectRatio === '1:1' ? '600x600' : aspectRatio === '4:5' ? '540x675' : aspectRatio === '9:16' ? '405x720' : aspectRatio === '16:9' ? '800x450' : '764x400'}/333333/${selectedBrand.color_primary.replace('#', '')}?text=Variation+3`,
          prompt: fullPrompt,
          aspectRatio,
          style,
          createdAt: Date.now(),
        },
        {
          id: `graphic-${Date.now()}-4`,
          imageUrl: `https://placehold.co/${aspectRatio === '1:1' ? '600x600' : aspectRatio === '4:5' ? '540x675' : aspectRatio === '9:16' ? '405x720' : aspectRatio === '16:9' ? '800x450' : '764x400'}/${selectedBrand.color_primary.replace('#', '')}/ffffff?text=Variation+4`,
          prompt: fullPrompt,
          aspectRatio,
          style,
          createdAt: Date.now(),
        },
      ];

      setGeneratedGraphics(mockGraphics);
      setSuccess('Generated 4 variations! Select one to use.');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to generate graphics. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSelectAndCreate = async () => {
    if (!selectedGraphic || !selectedBrand) return;

    try {
      // Create a new post with the selected graphic
      const response = await apiClient.createPost({
        brand_id: selectedBrand.id,
        title: headline || 'Social Graphic',
        content: prompt,
        platforms,
        media_urls: [selectedGraphic.imageUrl],
      });

      if (response.success && response.data?.id) {
        setSuccess('Graphic added to your posts!');
        setTimeout(() => {
          navigate(`/posts/${response.data!.id}`);
        }, 1000);
      }
    } catch (err) {
      setError('Failed to create post. Please try again.');
    }
  };

  const togglePlatform = (platform: SocialPlatform) => {
    setPlatforms(prev =>
      prev.includes(platform)
        ? prev.filter(p => p !== platform)
        : [...prev, platform]
    );
  };

  if (!selectedBrand) {
    return (
      <div className="flex items-center justify-center h-64">
        <Card className="text-center p-8">
          <Icon name="image" size="3xl" className="mx-auto mb-4 opacity-40" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Select a Brand</h2>
          <p className="text-gray-600 mb-4">
            Please select a brand from the dashboard to create graphics
          </p>
          <Button variant="primary" onClick={() => navigate('/dashboard')}>
            Go to Dashboard
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="outline" size="sm" onClick={() => navigate('/create')}>
          <Icon name="arrow-left" size="sm" className="mr-1" />
          Back
        </Button>
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Create Social Graphic</h1>
          <p className="text-gray-600 mt-1 text-sm sm:text-base">
            AI-powered graphics via{' '}
            <span className="font-medium text-teal-600">Kainva</span>
            {' '}for{' '}
            <span className="font-medium" style={{ color: selectedBrand.color_primary }}>
              {selectedBrand.name}
            </span>
          </p>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
          {success}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Form */}
        <div className="lg:col-span-2 space-y-6">
          {/* Main Prompt */}
          <Card>
            <CardHeader
              title="Describe Your Graphic"
              subtitle="Tell Kainva what you want to create"
            />
            <div className="space-y-4">
              <Textarea
                label="What should this graphic show?"
                placeholder="e.g., A promotional graphic for our summer sale with vibrant colors, featuring a beach theme and 30% off text overlay..."
                value={prompt}
                onChange={e => setPrompt(e.target.value)}
                rows={4}
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input
                  label="Headline (optional)"
                  placeholder="e.g., Summer Sale!"
                  value={headline}
                  onChange={e => setHeadline(e.target.value)}
                />
                <Input
                  label="Subheadline (optional)"
                  placeholder="e.g., Up to 30% off"
                  value={subheadline}
                  onChange={e => setSubheadline(e.target.value)}
                />
              </div>
            </div>
          </Card>

          {/* Style & Format */}
          <Card>
            <CardHeader title="Style & Format" subtitle="Choose the look and dimensions" />
            <div className="space-y-4">
              {/* Aspect Ratio */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Icon name="dashboard" size="sm" className="inline mr-1" />
                  Aspect Ratio
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
                  {ASPECT_RATIOS.map(ratio => (
                    <button
                      key={ratio.value}
                      onClick={() => setAspectRatio(ratio.value)}
                      className={`p-3 rounded-lg border-2 text-left transition-all ${
                        aspectRatio === ratio.value
                          ? 'border-teal-500 bg-teal-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className={`font-medium text-sm ${aspectRatio === ratio.value ? 'text-teal-700' : 'text-gray-700'}`}>
                        {ratio.label}
                      </div>
                      <div className="text-xs text-gray-500">{ratio.description}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Graphic Style */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Icon name="palette" size="sm" className="inline mr-1" />
                  Style
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {GRAPHIC_STYLES.map(s => (
                    <button
                      key={s.value}
                      onClick={() => setStyle(s.value)}
                      className={`p-3 rounded-lg border-2 text-left transition-all ${
                        style === s.value
                          ? 'border-teal-500 bg-teal-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className={`font-medium text-sm ${style === s.value ? 'text-teal-700' : 'text-gray-700'}`}>
                        {s.label}
                      </div>
                      <div className="text-xs text-gray-500">{s.description}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Options */}
              <div className="flex flex-wrap gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeText}
                    onChange={e => setIncludeText(e.target.checked)}
                    className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                  />
                  <Icon name="type" size="sm" className="opacity-50" />
                  <span className="text-sm text-gray-700">Include text overlay</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={useBrandColors}
                    onChange={e => setUseBrandColors(e.target.checked)}
                    className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                  />
                  <Icon name="palette" size="sm" className="opacity-50" />
                  <span className="text-sm text-gray-700">Use brand colors</span>
                </label>
              </div>
            </div>
          </Card>

          {/* Target Platforms */}
          <Card>
            <CardHeader title="Target Platforms" subtitle="Where will this graphic be posted?" />
            <div className="flex flex-wrap gap-2">
              {PLATFORM_OPTIONS.map(platform => (
                <button
                  key={platform.value}
                  onClick={() => togglePlatform(platform.value)}
                  className={`px-4 py-2 rounded-lg border-2 transition-all ${
                    platforms.includes(platform.value)
                      ? 'border-teal-500 bg-teal-50 text-teal-700'
                      : 'border-gray-200 hover:border-gray-300 text-gray-700'
                  }`}
                >
                  {platforms.includes(platform.value) && <Icon name="check" size="sm" className="inline mr-1" />}
                  {platform.label}
                </button>
              ))}
            </div>
          </Card>

          {/* Generate Button */}
          <Button
            className="w-full"
            size="lg"
            onClick={handleGenerate}
            isLoading={isGenerating}
            disabled={!prompt.trim()}
          >
            <Icon name="sparkles" size="md" className="mr-2" />
            Generate Graphics
          </Button>
        </div>

        {/* Right Column - Results */}
        <div className="lg:col-span-1">
          <Card className="sticky top-6">
            <CardHeader
              title="Generated Graphics"
              subtitle={generatedGraphics.length > 0 ? 'Select a variation to use' : 'Your graphics will appear here'}
              action={
                generatedGraphics.length > 0 && (
                  <Button size="sm" variant="outline" onClick={handleGenerate} disabled={isGenerating}>
                    <Icon name="refresh" size="sm" className={`mr-1 ${isGenerating ? 'animate-spin' : ''}`} />
                    Regenerate
                  </Button>
                )
              }
            />

            {generatedGraphics.length === 0 ? (
              <div className="text-center py-12">
                <Icon name="layers" size="3xl" className="mx-auto mb-4 opacity-30" />
                <p className="text-gray-500">No graphics generated yet</p>
                <p className="text-sm text-gray-400 mt-1">
                  Fill in the form and click generate
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Variations Grid */}
                <div className="grid grid-cols-2 gap-2">
                  {generatedGraphics.map((graphic, index) => (
                    <button
                      key={graphic.id}
                      onClick={() => setSelectedGraphic(graphic)}
                      className={`relative rounded-lg overflow-hidden border-2 transition-all ${
                        selectedGraphic?.id === graphic.id
                          ? 'border-teal-500 ring-2 ring-teal-200'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <img
                        src={graphic.imageUrl}
                        alt={`Variation ${index + 1}`}
                        className="w-full aspect-square object-cover"
                      />
                      {selectedGraphic?.id === graphic.id && (
                        <div className="absolute top-2 right-2 bg-teal-500 text-white rounded-full p-1">
                          <Icon name="check" size="xs" />
                        </div>
                      )}
                      <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-xs py-1 text-center">
                        Variation {index + 1}
                      </div>
                    </button>
                  ))}
                </div>

                {/* Selected Preview */}
                {selectedGraphic && (
                  <div className="border-t pt-4">
                    <p className="text-sm font-medium text-gray-700 mb-2">Selected Preview</p>
                    <img
                      src={selectedGraphic.imageUrl}
                      alt="Selected graphic"
                      className="w-full rounded-lg"
                    />
                    <div className="flex gap-2 mt-4">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={() => {
                          // Download the image
                          const a = document.createElement('a');
                          a.href = selectedGraphic.imageUrl;
                          a.download = `graphic-${selectedGraphic.id}.png`;
                          a.click();
                        }}
                      >
                        <Icon name="download" size="sm" className="mr-1" />
                        Download
                      </Button>
                      <Button
                        size="sm"
                        className="flex-1"
                        onClick={handleSelectAndCreate}
                      >
                        <Icon name="check" size="sm" className="mr-1" />
                        Use This
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </Card>
        </div>
      </div>

      {/* Info Card */}
      <Card className="bg-teal-50 border-teal-200">
        <div className="p-6">
          <h3 className="text-lg font-semibold text-teal-900 mb-2">
            Powered by Kainva
          </h3>
          <p className="text-teal-800">
            Kainva uses AI to generate professional social media graphics from natural language descriptions.
            Your brand colors, style, and preferences are automatically applied for consistent visual identity.
          </p>
        </div>
      </Card>
    </div>
  );
}
